import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';

// ─── Common lab-attendant duties (static reminder card) ───
const LAB_DUTIES = [
  { icon: 'fa-door-open',   text: 'Open lab 15 min before first practical, ensure benches are clean.' },
  { icon: 'fa-boxes',       text: 'Verify apparatus, chemicals, and consumables against the register.' },
  { icon: 'fa-flask',       text: 'Set up reagents and glassware per the day\'s practical schedule.' },
  { icon: 'fa-shield-alt',  text: 'Confirm fire extinguisher, first-aid kit, and eye-wash station are functional.' },
  { icon: 'fa-clipboard-check', text: 'Record any breakage or missing item and report to the HOD.' },
  { icon: 'fa-lock',        text: 'Lock chemical cabinets and shut off gas/water mains at end of day.' },
];

export default function LabAttendantDashboard() {
  const { user } = useAuth();

  const [announcements, setAnnouncements] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Load data ──
  useEffect(() => {
    Promise.all([
      api.get('/announcements/').catch(() => ({ data: [] })),
      api.get('/staff-attendance/my-records').catch(() => ({ data: [] })),
    ])
      .then(([a, att]) => {
        setAnnouncements(a.data || []);
        setAttendance(att.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Derived stats ──
  const todayStr = new Date().toISOString().split('T')[0];
  const monthKey = todayStr.slice(0, 7);

  const stats = useMemo(() => {
    const todayRecord = attendance.find(r => r.date === todayStr);
    const monthRecords = attendance.filter(r => r.date && r.date.startsWith(monthKey));
    const monthPresent = monthRecords.filter(
      r => r.status === 'Present' || r.status === 'Late'
    ).length;
    const monthTotal = monthRecords.length;
    const rate = monthTotal > 0 ? Math.round((monthPresent / monthTotal) * 100) : 0;

    return {
      todayRecord,
      monthTotal,
      monthPresent,
      rate,
    };
  }, [attendance, todayStr, monthKey]);

  const unread = announcements.filter(a => !a.is_read).length;
  const recent = announcements.slice(0, 5);

  // ── Render ──
  return (
    <div>
      {/* ═══════════ HERO ═══════════ */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white p-6 rounded-lg shadow mb-6 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-56 h-56 bg-white/5 rounded-full"></div>
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white/5 rounded-full"></div>
        <div className="flex items-center gap-4 relative z-10">
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <i className="fas fa-flask text-2xl"></i>
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Laboratory Desk</h1>
            <p className="text-cyan-100 text-sm mt-1">
              Welcome back, {user?.first_name || 'Lab Attendant'} — {new Date().toLocaleDateString('en', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>
      </div>

      {/* ═══════════ STATS ROW ═══════════ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {/* Today's status */}
        <div className={`rounded-lg shadow p-4 border-l-4 ${
          stats.todayRecord?.status === 'Present' || stats.todayRecord?.status === 'Late'
            ? 'bg-green-50 border-green-500'
            : stats.todayRecord?.status === 'Absent'
              ? 'bg-red-50 border-red-500'
              : 'bg-gray-50 border-gray-400'
        }`}>
          <div className="flex items-center gap-2 mb-1">
            <i className={`fas ${
              stats.todayRecord?.status === 'Present' ? 'fa-check-circle text-green-600'
                : stats.todayRecord?.status === 'Late' ? 'fa-clock text-yellow-600'
                  : stats.todayRecord?.status === 'Absent' ? 'fa-times-circle text-red-600'
                    : 'fa-question-circle text-gray-500'
            }`}></i>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Today</span>
          </div>
          <p className={`text-lg font-bold ${
            stats.todayRecord?.status === 'Present' || stats.todayRecord?.status === 'Late' ? 'text-green-700'
              : stats.todayRecord?.status === 'Absent' ? 'text-red-700'
                : 'text-gray-700'
          }`}>
            {stats.todayRecord?.status || 'Not Marked'}
          </p>
          {stats.todayRecord?.check_in_local && (
            <p className="text-[11px] text-gray-500 mt-0.5">
              In: {stats.todayRecord.check_in_local}
              {stats.todayRecord.check_out_local && ` · Out: ${stats.todayRecord.check_out_local}`}
            </p>
          )}
        </div>

        {/* Month attendance rate */}
        <div className="rounded-lg shadow p-4 bg-white border-l-4 border-blue-500">
          <div className="flex items-center gap-2 mb-1">
            <i className="fas fa-percent text-blue-600"></i>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">This Month</span>
          </div>
          <p className="text-lg font-bold text-blue-700">{stats.rate}%</p>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {stats.monthPresent}/{stats.monthTotal} days present
          </p>
        </div>

        {/* Announcements */}
        <div className="rounded-lg shadow p-4 bg-white border-l-4 border-cyan-500">
          <div className="flex items-center gap-2 mb-1">
            <i className="fas fa-bullhorn text-cyan-600"></i>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Announcements</span>
          </div>
          <p className="text-lg font-bold text-cyan-700">{announcements.length}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">Total notices</p>
        </div>

        {/* Unread */}
        <div className="rounded-lg shadow p-4 bg-white border-l-4 border-purple-500">
          <div className="flex items-center gap-2 mb-1">
            <i className="fas fa-envelope text-purple-600"></i>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Unread</span>
          </div>
          <p className="text-lg font-bold text-purple-700">{unread}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">New notices</p>
        </div>
      </div>

      {/* ═══════════ QUICK ACTIONS ═══════════ */}
      <div className="bg-white p-6 rounded shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">
          <i className="fas fa-bolt text-yellow-500 mr-2"></i>
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link
            to="/dashboard/notifications"
            className="bg-cyan-500 text-white p-5 rounded-lg text-center hover:bg-cyan-600 transition shadow-sm"
          >
            <i className="fas fa-bell text-3xl mb-2"></i>
            <p className="font-semibold">Announcements</p>
            <p className="text-xs text-cyan-100 mt-1">
              {unread > 0 ? `${unread} unread notice${unread !== 1 ? 's' : ''}` : 'Read school notices'}
            </p>
          </Link>

          <Link
            to="/dashboard/my-attendance"
            className="bg-purple-500 text-white p-5 rounded-lg text-center hover:bg-purple-600 transition shadow-sm"
          >
            <i className="fas fa-user-clock text-3xl mb-2"></i>
            <p className="font-semibold">My Attendance</p>
            <p className="text-xs text-purple-100 mt-1">
              Check-in/out history & monthly summary
            </p>
          </Link>
        </div>
      </div>

      {/* ═══════════ TWO-COLUMN: Duties + Announcements ═══════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Lab Duties Checklist */}
        <div className="bg-white p-6 rounded shadow lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              <i className="fas fa-clipboard-check text-teal-500 mr-2"></i>
              Daily Lab Duties
            </h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Common tasks to keep the lab ready and safe.
          </p>
          <div className="space-y-3">
            {LAB_DUTIES.map((d, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center flex-shrink-0">
                  <i className={`fas ${d.icon} text-xs`}></i>
                </div>
                <p className="text-sm text-gray-700 leading-snug">{d.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Announcements */}
        <div className="bg-white p-6 rounded shadow lg:col-span-2">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              <i className="fas fa-bullhorn text-cyan-500 mr-2"></i>
              Recent Announcements
            </h2>
            <Link
              to="/dashboard/notifications"
              className="text-sm text-cyan-600 hover:underline"
            >
              View all →
            </Link>
          </div>

          {loading ? (
            <p className="text-gray-500 text-center py-8">Loading…</p>
          ) : recent.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <i className="fas fa-bell-slash text-4xl mb-3"></i>
              <p>No announcements yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recent.map(a => (
                <div key={a.id} className="border-l-4 border-cyan-400 pl-3 py-2">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-800">{a.title}</p>
                    {!a.is_read && (
                      <span className="text-[10px] bg-red-500 text-white px-2 py-0.5 rounded-full font-bold">
                        NEW
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mt-1">{a.message}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {a.posted_by_name} · {a.created_at_display}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════ LAB SAFETY FOOTER ═══════════ */}
      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-500 p-4 rounded shadow">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div>
            <p className="font-semibold text-amber-900">Lab Safety Reminder</p>
            <p className="text-sm text-amber-800 mt-0.5">
              In case of any accident, spill, or equipment failure — report immediately to the HOD and the principal's office. Never leave students unattended in the lab.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}